
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUp } from "lucide-react";
import CustomCursor from "../components/CustomCursor";
import Navigation from "../components/Navigation";
import HeroSection from "../components/HeroSection";
import SkillsSection from "../components/SkillsSection";
import ProjectCard from "../components/ProjectCard";
import ContactForm from "../components/ContactForm";
import ImprovedFooter from "../components/ImprovedFooter";
import profileImage from "./Profile.jpeg";
import backgroundImage from "./1.jpg";
import { motion } from "framer-motion";
import ShoeStoreImage from "./shoestore.png";

const Index = () => {
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollToTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const projects = [
    {
      title: "Movie Recommendation Application - MoviCritics",
      description:
        "Developed and deployed a movie recommendation platform using PHP, React.js, HTML/CSS, and MySQL across Linux-based VM clusters. Implemented RESTful APIs with DMZ clustering and caching to reduce database load by 30%, secured the application with ZeroTier VPN, two-factor authentication, and encrypted sessions, and enabled real-time comments and automated notifications via RabbitMQ.",
      tags: ["PHP", "React", "HTML", "CSS", "MySQL", "ZeroTier", "RabbitMQ", "API", "System Security"],
      image: "/images/movie-search.jpg",
      github: "https://github.com/healer3771/Movie-Recommendation-Application",
      demo: "/projects/movie-search",
    },
    {
      title: "Dynamic Online Shoe Store",
      description:
        "Built a responsive e-commerce platform using PHP, HTML, CSS, and SQL. Deployed MySQL on AWS EC2 to manage products, customers, and orders, optimized database queries for performance, and implemented advanced product search, automated shipping label generation, and an admin dashboard for inventory and order management.",
      tags: ["PHP", "HTML", "CSS", "SQL", "AWS EC2", "E-Commerce"],
      image: "./ShoeStoreProject.png",
      github: "https://github.com/healer3771/Dynamic_Online_Shoe_Store_Website",
      demo: "/projects/online-shoe-store",
    },
    {
      title: "E-Commerce Website Development",
      description:
        "Designed and developed a customized e-commerce website using Wix, integrating brand-specific designs, PayPal and Stripe payment gateways, and optimizing user engagement through search functionality and seamless navigation.",
      tags: ["Wix", "E-Commerce", "Design", "PayPal", "Stripe"],
      image: "/images/online-store.jpg",
      github: "https://github.com/your-github/online-store",
      demo: "/projects/shoe-store",
    },
    {
      title: "Global Chatroom Application",
      description:
        "Designed and developed a real-time virtual chat application using Java, JavaScript, and Node.js. Implemented secure authentication, private messaging, persistent mute lists, chat history export with 7-day log storage, and optimized performance to achieve 30% faster message delivery.",
      tags: ["Java", "Node.js", "JavaScript", "Chatroom", "Real-Time Communication", "Security"],
      image: "/images/chatroom.jpg",
      github: "https://github.com/healer3771/Global_Chatroom_Application",
      demo: "/projects/chatroom",
    },
    {
      title: "Interactive 3D Game Interface (Unity)",
      description:
        "Designed and developed an immersive 3D game interface using Unity, integrating real-time clocks, day-night cycle simulations, and dynamic 3D environments focused on interactive user engagement and realistic transitions.",
      tags: ["Unity", "3D Design", "Game Development"],
      image: "/images/game-interface.jpg",
      github: "https://github.com/healer3771/Interactive-3D-Game-Interface-Development",
      demo: "/projects/game-interface",
    },
  ];  

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat text-white font-sans relative w-full overflow-x-hidden"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundColor: "#121212",
      }}
    >
      <CustomCursor />
      <Navigation />

      {loading ? (
        <div className="flex justify-center items-center min-h-screen bg-black bg-opacity-80">
          <div className="loader"></div>
        </div>
      ) : (
        <>
          <div id="welcome" className="absolute inset-0 bg-gradient-to-b from-black/70 to-transparent pointer-events-none"></div>
          
          <div className="relative z-10">
            <HeroSection profileImage={profileImage} />
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20 py-20">
              <SkillsSection />
              
              <motion.div 
                id="projects" 
                className="bg-black bg-opacity-80 p-8 rounded-lg shadow-lg"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <h2 className="text-2xl font-bold text-yellow-300 mb-8 flex items-center">
                  <span className="inline-block w-8 h-1 bg-yellow-300 mr-3"></span>
                  Featured Projects
                  <span className="inline-block w-8 h-1 bg-yellow-300 ml-3"></span>
                </h2>
                
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {projects.map((project, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <ProjectCard {...project} />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
              
              <ContactForm />
            </div>
            
            <ImprovedFooter />
          </div>
          
          {showScrollToTop && (
            <button
              onClick={scrollToTop}
              className="fixed bottom-8 right-8 bg-yellow-300 p-3 rounded-full shadow-lg hover:bg-yellow-400 transition z-50"
              aria-label="Scroll to top"
            >
              <ArrowUp className="text-black" />
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default Index;
